import { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
  faCircleInfo,
  faClockRotateLeft,
  faMessage,
  faPlus,
  faXmark,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { CreditUsageReminder } from '@/components/ai/credit-usage-reminder';
import { ImageCropModal } from '@/components/asset-manager/components/image-crop-modal';
import type { CropData } from '@/components/asset-manager/components/image-crop-modal/types';
import { Conversation, ConversationContent } from '@/components/ui/ai-elements/conversation';
import { LoadingIndicator, MessageResponse } from '@/components/ui/ai-elements/message';
import {
  PromptInput,
  PromptInputFooter,
  type PromptInputMessage,
  PromptInputSubmit,
  PromptInputTextarea,
} from '@/components/ui/ai-elements/prompt-input';
import { Suggestion, Suggestions } from '@/components/ui/ai-elements/suggestion';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Icon } from '@/components/ui/icon';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DestructiveText, ExtraSmallText, Heading3, MutedText } from '@/components/ui/typography';
import { findPendingImageConfirmation } from '@/lib/assistant/image-generation-confirmation';
import { REQUEST_CANCELLED_TEXT } from '@/lib/assistant/message-turn';
import { cn } from '@/lib/utils';
import type { CreditStatus } from '@/types/shared';
import type { ConversationItem, ImageConversationItem } from './types';

const THREAD_REFRESH_INTERVAL_MS = 30_000;

export interface ThreadItem {
  id: string;
  title?: string | null;
  createdAt?: Date | string;
  updatedAt?: Date | string;
}

interface ThreadGroup {
  label: string;
  items: ThreadItem[];
}

export interface PromptSuggestion {
  label: string;
  prompt: string;
}

interface ThreadControlsProps {
  isLoadingThreads: boolean;
  isStreaming: boolean;
  messageCount: number;
  threads: ThreadItem[];
  threadId: string;
  onCreateThread: () => void;
  onHistoryOpenChange: (open: boolean) => void;
  onSelectThread: (threadId: string) => void;
}

function ThreadControls({
  isLoadingThreads,
  isStreaming,
  messageCount,
  threads,
  threadId,
  onCreateThread,
  onHistoryOpenChange,
  onSelectThread,
}: ThreadControlsProps) {
  const isEmptyThread = messageCount === 0;
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const lastRefreshAtRef = useRef<number | null>(null);

  const handleHistoryOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsHistoryOpen(nextOpen);
      if (!nextOpen) return;
      const now = Date.now();
      if (
        !lastRefreshAtRef.current ||
        now - lastRefreshAtRef.current > THREAD_REFRESH_INTERVAL_MS
      ) {
        lastRefreshAtRef.current = now;
        onHistoryOpenChange(true);
      }
    },
    [onHistoryOpenChange],
  );

  const groupedThreads: ThreadGroup[] = useMemo(() => {
    const now = Date.now();
    const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const groups: ThreadGroup[] = [
      { label: 'Today', items: [] },
      { label: 'Yesterday', items: [] },
      { label: 'Earlier', items: [] },
    ];

    threads.forEach((thread) => {
      const lastActivityRaw = thread.updatedAt ?? thread.createdAt;
      const lastActivity = lastActivityRaw ? new Date(lastActivityRaw) : null;
      if (!lastActivity || Number.isNaN(lastActivity.getTime())) return;
      if (now - lastActivity.getTime() > ONE_WEEK_MS) return;

      if (lastActivity >= startOfToday) {
        groups[0].items.push(thread);
      } else if (lastActivity >= startOfYesterday) {
        groups[1].items.push(thread);
      } else {
        groups[2].items.push(thread);
      }
    });

    return groups.filter((group) => group.items.length > 0);
  }, [threads]);

  return (
    <div className="flex items-center gap-1">
      {isEmptyThread || isStreaming ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button variant="ghost" size="sm" title="New thread" disabled>
                <Icon icon={faPlus} /> New
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>
            {isEmptyThread
              ? 'Send a message to start a new thread.'
              : 'Wait for the current response to finish.'}
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button variant="ghost" size="sm" onClick={onCreateThread} title="New thread">
          <Icon icon={faPlus} /> New
        </Button>
      )}
      <Separator orientation="vertical" />
      {isStreaming ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                variant="ghost"
                size="sm"
                title="Thread history"
                aria-expanded={false}
                disabled
              >
                <Icon icon={faClockRotateLeft} /> History
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent sideOffset={6}>Wait for the current response to finish.</TooltipContent>
        </Tooltip>
      ) : (
        <DropdownMenu onOpenChange={handleHistoryOpenChange}>
          <div className="flex items-center">
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                title="Thread history"
                aria-expanded={isHistoryOpen}
                className={cn(isHistoryOpen && 'bg-accent text-accent-foreground')}
              >
                <Icon icon={faClockRotateLeft} /> History
              </Button>
            </DropdownMenuTrigger>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="flex items-center px-1 text-muted-foreground">
                  <Icon icon={faCircleInfo} className="size-3" />
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-52">
                Generated images expire after 7 days. Save any you want to keep to your asset
                library.
              </TooltipContent>
            </Tooltip>
          </div>
          <DropdownMenuContent align="end" className="max-h-80 min-w-64 overflow-y-auto">
            {isLoadingThreads ? (
              <DropdownMenuItem disabled>Loading threads...</DropdownMenuItem>
            ) : groupedThreads.length === 0 ? (
              <DropdownMenuItem disabled>No threads yet</DropdownMenuItem>
            ) : (
              <DropdownMenuRadioGroup value={threadId} onValueChange={onSelectThread}>
                {groupedThreads.map((group, groupIndex) => (
                  <DropdownMenuGroup key={group.label}>
                    <DropdownMenuLabel className="py-1 text-xs">{group.label}</DropdownMenuLabel>
                    {group.items.map((thread, index) => (
                      <DropdownMenuRadioItem
                        key={thread.id}
                        value={thread.id}
                        className="truncate pr-8"
                        title={thread.title?.trim() || `Thread ${index + 1}`}
                      >
                        <Icon icon={faMessage} /> {thread.title?.trim() || `Thread ${index + 1}`}
                      </DropdownMenuRadioItem>
                    ))}
                    {groupIndex < groupedThreads.length - 1 ? <DropdownMenuSeparator /> : null}
                  </DropdownMenuGroup>
                ))}
              </DropdownMenuRadioGroup>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

export interface AiImageAssistantPanelHandle {
  focusInput: () => void;
  // No-op if no image exists in the conversation.
  openCropModal: () => void;
  openCropModalForImage: (item: ImageConversationItem) => void;
}

export interface AiImageAssistantCropModalProps {
  imageUrl: string;
  isOpen: boolean;
  onSave: (cropData: CropData) => Promise<void>;
  onCancel: () => void;
  showUpscale?: boolean;
  showRemoveBg?: boolean;
  saveLabel?: string;
}

export interface AiImageAssistantPanelProps {
  conversation?: ConversationItem[];
  onGenerate: (prompt: string) => void | Promise<void>;
  onImageClick?: (item: ImageConversationItem) => void;
  onImageError?: (imageId: string) => void;
  onOpenCropRequest?: (item: ImageConversationItem) => void;
  expiredImageIds?: ReadonlySet<string>;
  cropModal?: AiImageAssistantCropModalProps | null;
  isGenerating?: boolean;
  isLoadingHistory?: boolean;
  creditStatus?: CreditStatus | null;

  // Controlled input — when omitted the panel manages its own input state.
  promptValue?: string;
  onPromptChange?: (value: string) => void;

  onClose?: () => void;
  onStop?: () => void;

  title?: string;
  emptyStateText?: string;
  inputPlaceholder?: string;
  inputPlaceholderAfterFirstMessage?: string;
  promptSuggestions?: PromptSuggestion[];
  promptSuggestionsLabel?: string;
  modelLabel?: string;
  maxPromptLength?: number;

  threads?: ThreadItem[];
  isLoadingThreads?: boolean;
  threadId?: string;
  onCreateThread?: () => void;
  onHistoryOpenChange?: (open: boolean) => void;
  onSelectThread?: (threadId: string) => void;

  className?: string;
  ref?: React.Ref<AiImageAssistantPanelHandle>;
}

const DEFAULT_MAX_PROMPT_LENGTH = 1_000;
const DEFAULT_EMPTY_STATE =
  'Let AI guide you in creating and customizing images for your site easily.';
const DEFAULT_INPUT_PLACEHOLDER = 'Describe the image you want to generate';
const DEFAULT_AFTER_FIRST_PLACEHOLDER = 'Start typing or choose from the suggestions above';

function ImageConversationLoadingSkeleton() {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <Skeleton className="h-[66px] w-4/5 rounded-[31px]" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-[151px] w-3/4 rounded-lg" />
      </div>
      <div className="flex justify-end">
        <Skeleton className="h-12 w-2/3 rounded-[31px]" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-[151px] w-2/3 rounded-lg" />
      </div>
    </div>
  );
}

export function AiImageAssistantPanel({
  conversation = [],
  onGenerate,
  onImageClick,
  onImageError,
  onOpenCropRequest,
  expiredImageIds,
  cropModal,
  isGenerating = false,
  isLoadingHistory = false,
  creditStatus,
  promptValue,
  onPromptChange,
  onClose,
  onStop,
  title = 'AI Image Assistant',
  emptyStateText = DEFAULT_EMPTY_STATE,
  inputPlaceholder = DEFAULT_INPUT_PLACEHOLDER,
  inputPlaceholderAfterFirstMessage = DEFAULT_AFTER_FIRST_PLACEHOLDER,
  promptSuggestions,
  promptSuggestionsLabel = 'Refine prompt',
  modelLabel,
  maxPromptLength = DEFAULT_MAX_PROMPT_LENGTH,
  threads: threadsProp,
  isLoadingThreads: isLoadingThreadsProp,
  threadId: threadIdProp,
  onCreateThread: onCreateThreadProp,
  onHistoryOpenChange: onHistoryOpenChangeProp,
  onSelectThread: onSelectThreadProp,
  className,
  ref,
}: AiImageAssistantPanelProps) {
  const threads = threadsProp;
  const isLoadingThreads = isLoadingThreadsProp;
  const threadId = threadIdProp;
  const onCreateThread = onCreateThreadProp;
  const onSelectThread = onSelectThreadProp;
  const onHistoryOpenChange = onHistoryOpenChangeProp;
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [internalPrompt, setInternalPrompt] = useState('');
  const isControlled = promptValue !== undefined;
  const currentPrompt = isControlled ? promptValue : internalPrompt;
  const lastImageItem = [...conversation].reverse().find((item) => item.type === 'image') as
    | ImageConversationItem
    | undefined;
  const lastUserItem = [...conversation].reverse().find((item) => item.type === 'user') as
    | { id: string; type: 'user'; text: string }
    | undefined;
  const imageConfirmation = findPendingImageConfirmation(conversation);
  const isAwaitingPeopleImageChoice = !!imageConfirmation;

  useImperativeHandle(
    ref,
    () => ({
      focusInput: () => {
        setTimeout(() => inputRef.current?.focus(), 0);
      },
      openCropModal: () => {
        if (lastImageItem) {
          onOpenCropRequest?.(lastImageItem);
        }
      },
      openCropModalForImage: (item: ImageConversationItem) => {
        onOpenCropRequest?.(item);
      },
    }),
    [lastImageItem, onOpenCropRequest],
  );

  const isBusy = isGenerating;
  const creditsExhausted = creditStatus?.level === 'exhausted';

  const showThreadControls =
    threads !== undefined &&
    threadId !== undefined &&
    onCreateThread !== undefined &&
    onHistoryOpenChange !== undefined &&
    onSelectThread !== undefined;

  const updatePrompt = (next: string) => {
    const capped = next.slice(0, maxPromptLength);
    if (!isControlled) {
      setInternalPrompt(capped);
    }
    onPromptChange?.(capped);
  };

  const handleSubmit = ({ text }: PromptInputMessage) => {
    if (creditsExhausted) return;
    void onGenerate(text);
    if (!isControlled) {
      setInternalPrompt('');
    }
    onPromptChange?.('');
  };

  const placeholder =
    conversation.length > 0 ? inputPlaceholderAfterFirstMessage : inputPlaceholder;

  return (
    <>
      {cropModal && (
        <ImageCropModal
          imageUrl={cropModal.imageUrl}
          isOpen={cropModal.isOpen}
          onSave={cropModal.onSave}
          onCancel={cropModal.onCancel}
          showUpscale={cropModal.showUpscale}
          showRemoveBg={cropModal.showRemoveBg}
          saveLabel={cropModal.saveLabel}
        />
      )}
      <aside
        className={cn('flex h-full max-h-full min-h-0 flex-col bg-background', className)}
        aria-label="AI Image Assistant"
      >
        <div className="relative flex shrink-0 items-center gap-2 p-6 pr-12">
          <Heading3 className="text-lg font-bold text-foreground">{title}</Heading3>
          {showThreadControls && (
            <ThreadControls
              isLoadingThreads={isLoadingThreads ?? false}
              isStreaming={isGenerating}
              messageCount={conversation.length}
              threads={threads}
              threadId={threadId}
              onCreateThread={onCreateThread}
              onHistoryOpenChange={onHistoryOpenChange}
              onSelectThread={onSelectThread}
            />
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Close panel"
              onClick={onClose}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Icon icon={faXmark} className="size-4" />
            </Button>
          )}
        </div>

        {isLoadingHistory ? (
          <Conversation messageCount={4} className="min-h-0 px-6 pb-6">
            <ConversationContent className="gap-3 p-0">
              <ImageConversationLoadingSkeleton />
            </ConversationContent>
          </Conversation>
        ) : conversation.length === 0 ? (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 pb-6">
            <MutedText className="text-center text-base">{emptyStateText}</MutedText>
          </div>
        ) : (
          <Conversation messageCount={conversation.length} className="min-h-0 px-6 pb-6">
            <ConversationContent className="gap-3 p-0">
              {conversation.map((item) => {
                if (item.type === 'user') {
                  return (
                    <div key={item.id} className="flex justify-end">
                      <div className="rounded-[31px] bg-muted px-6 py-3 text-sm text-foreground">
                        {item.text}
                      </div>
                    </div>
                  );
                }

                if (item.type === 'assistant') {
                  if (item.text === REQUEST_CANCELLED_TEXT) {
                    return (
                      <div key={item.id} className="flex justify-start">
                        <MutedText className="italic">{REQUEST_CANCELLED_TEXT}</MutedText>
                      </div>
                    );
                  }
                  return (
                    <div key={item.id} className="flex justify-start">
                      <MessageResponse className="prose prose-sm max-w-[85%] text-sm text-foreground [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
                        {item.text}
                      </MessageResponse>
                    </div>
                  );
                }

                if (item.type === 'thinking') {
                  return <LoadingIndicator key={item.id} />;
                }

                if (item.type === 'editor-open') {
                  return (
                    <div key={item.id} className="flex justify-start">
                      <MutedText className="text-sm">{item.text}</MutedText>
                    </div>
                  );
                }

                if (item.type === 'generating') {
                  return (
                    <div key={item.id} className="flex flex-col gap-2">
                      <MutedText>Generating image...</MutedText>
                      <Skeleton className="h-[151px] w-3/4 rounded-lg" />
                    </div>
                  );
                }

                if (item.type === 'error') {
                  return (
                    <DestructiveText key={item.id} className="text-sm">
                      {item.text}
                    </DestructiveText>
                  );
                }

                if (item.type === 'credit_notice') {
                  return (
                    <div key={item.id}>
                      <CreditUsageReminder status={item.creditNotice} presentation="transcript" />
                    </div>
                  );
                }

                if (item.type === 'image') {
                  const isExpired = expiredImageIds?.has(item.id) ?? false;
                  return (
                    <div key={item.id} className="flex flex-col gap-2">
                      <p className="text-sm text-muted-foreground">Image generated</p>
                      {isExpired ? (
                        <p className="text-sm italic text-muted-foreground">[Image expired]</p>
                      ) : onImageClick ? (
                        <Button
                          variant="ghost"
                          className="h-auto w-fit overflow-hidden rounded-lg border border-border bg-muted/20 p-0 hover:bg-muted/20 hover:border-primary/60"
                          onClick={() => onImageClick(item)}
                          aria-label="Open image in editor"
                        >
                          <img
                            src={item.imageUrl}
                            alt={item.prompt}
                            className="block h-[151px] w-auto object-contain"
                            onError={() => onImageError?.(item.id)}
                          />
                        </Button>
                      ) : (
                        <div className="w-fit overflow-hidden rounded-lg border border-border bg-muted/20">
                          <img
                            src={item.imageUrl}
                            alt={item.prompt}
                            className="block h-[151px] w-auto object-contain"
                            onError={() => onImageError?.(item.id)}
                          />
                        </div>
                      )}
                    </div>
                  );
                }

                return null;
              })}
            </ConversationContent>
          </Conversation>
        )}

        <div className="flex shrink-0 flex-col gap-2.5 border-t border-border px-6 pb-6 pt-4">
          <CreditUsageReminder status={creditStatus} />
          {isAwaitingPeopleImageChoice && (
            <Suggestions className="flex-row flex-wrap gap-2.5">
              {imageConfirmation?.choices.map((choice) => (
                <Suggestion
                  key={choice.id}
                  suggestion={choice.label}
                  className="w-auto max-w-full whitespace-nowrap px-3 line-clamp-none"
                  onClick={() => void onGenerate(choice.label)}
                  disabled={isBusy || creditsExhausted}
                />
              ))}
            </Suggestions>
          )}

          {lastUserItem && !isAwaitingPeopleImageChoice && !promptSuggestions?.length && (
            <Suggestions className="flex-row flex-wrap gap-2.5">
              <Suggestion
                suggestion="Retry Prompt"
                className="w-auto max-w-full whitespace-nowrap px-3 line-clamp-none"
                onClick={() => void onGenerate(lastUserItem.text)}
                disabled={isBusy || creditsExhausted}
              />
              <Suggestion
                suggestion="Adjust Prompt"
                className="w-auto max-w-full whitespace-nowrap px-3 line-clamp-none"
                onClick={() => {
                  updatePrompt(lastUserItem.text);
                  setTimeout(() => inputRef.current?.focus(), 0);
                }}
                disabled={isBusy || creditsExhausted}
              />
            </Suggestions>
          )}

          {promptSuggestions && promptSuggestions.length > 0 && (
            <div className="flex flex-col gap-2">
              <ExtraSmallText className="px-1 text-muted-foreground">
                {promptSuggestionsLabel}
              </ExtraSmallText>
              <Suggestions className="flex-row flex-wrap gap-2.5">
                {promptSuggestions.map((suggestion) => (
                  <Suggestion
                    key={`${suggestion.label}:${suggestion.prompt}`}
                    suggestion={suggestion.label}
                    className="w-auto max-w-full whitespace-nowrap px-3 line-clamp-none"
                    onClick={() => {
                      updatePrompt(suggestion.prompt);
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }}
                    disabled={isBusy || creditsExhausted}
                  />
                ))}
              </Suggestions>
            </div>
          )}

          {modelLabel && <span className="text-xs text-muted-foreground">{modelLabel}</span>}
          <PromptInput onSubmit={handleSubmit} isLoading={isBusy}>
            <PromptInputFooter>
              <PromptInputTextarea
                ref={inputRef}
                placeholder={creditsExhausted ? 'AI credits are exhausted' : placeholder}
                aria-label="Describe the image you want to generate"
                isLoading={isGenerating}
                value={currentPrompt}
                onChange={(e) => updatePrompt(e.target.value)}
                disabled={isGenerating || creditsExhausted}
              />
              <PromptInputSubmit
                isLoading={isGenerating}
                onStop={onStop}
                disabled={creditsExhausted || (!isGenerating && !currentPrompt.trim())}
              />
            </PromptInputFooter>
          </PromptInput>
        </div>
      </aside>
    </>
  );
}
